import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, X } from "lucide-react";
import { API } from "../constants";
import { PhotoUpload } from "../components/PhotoUpload";

export const RestaurantFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [cuisineTypesList, setCuisineTypesList] = useState([]);
  const [specialtiesList, setSpecialtiesList] = useState([]);
  const [form, setForm] = useState({
    name: "",
    address: "",
    city: "",
    province: "",
    postal_code: "",
    country: "",
    phone_code: "+34",
    phone: "",
    website: "",
    instagram: "",
    cuisine_type: "",
    specialties: [],
    description: "",
    main_photo: "",
    dish_photos: []
  });

  useEffect(() => {
    fetchConfigData();
    if (isEdit) {
      fetchRestaurant();
    }
  }, [id]);

  const fetchConfigData = async () => {
    try {
      const [cuisineRes, specialtiesRes] = await Promise.all([
        axios.get(`${API}/config/cuisine-types`),
        axios.get(`${API}/config/specialties`)
      ]);
      setCuisineTypesList(cuisineRes.data);
      setSpecialtiesList(specialtiesRes.data);
    } catch (err) {
      console.error("Error loading config data");
    }
  };

  const fetchRestaurant = async () => {
    try {
      const res = await axios.get(`${API}/restaurants/${id}`);
      const data = res.data;
      
      // Parse phone into code and number
      let phone_code = "+34";
      let phone = "";
      if (data.phone && data.phone.includes(" ")) {
        const parts = data.phone.split(" ");
        phone_code = parts[0];
        const digits = parts.slice(1).join("").replace(/\D/g, '');
        phone = digits.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
      } else if (data.phone) {
        const digits = data.phone.replace(/\D/g, '');
        phone = digits.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
      }
      
      setForm({ 
        ...data, 
        specialties: data.specialties || [],
        phone_code,
        phone
      });
    } catch (err) {
      toast.error("Error al cargar restaurante");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const toggleSpecialty = (specialty) => {
    const current = form.specialties || [];
    if (current.includes(specialty)) {
      setForm({ ...form, specialties: current.filter(s => s !== specialty) });
    } else {
      setForm({ ...form, specialties: [...current, specialty] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Combine phone code and number
      const submitData = {
        ...form,
        phone: form.phone ? `${form.phone_code} ${form.phone}` : ""
      };
      delete submitData.phone_code;
      
      if (isEdit) {
        await axios.put(`${API}/restaurants/${id}`, submitData);
        toast.success("Restaurante actualizado");
      } else {
        await axios.post(`${API}/restaurants`, submitData);
        toast.success("Restaurante añadido");
      }
      navigate("/");
    } catch (err) {
      toast.error("Error al guardar");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhotoUpload = (url) => {
    setForm({ ...form, main_photo: url });
  };

  const handleDishPhotosUpload = (urls) => {
    setForm({ ...form, dish_photos: [...form.dish_photos, ...urls] });
  };

  const removeDishPhoto = (index) => {
    setForm({
      ...form,
      dish_photos: form.dish_photos.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" data-testid="restaurant-form-page">
      <header className="sticky top-0 z-40 glass-nav p-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <button onClick={() => navigate(-1)} data-testid="back-btn">
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-xl font-semibold text-amber-500">
            {isEdit ? "Editar Restaurante" : "Añadir Restaurante"}
          </h1>
        </div>
      </header>

      <main className="p-4 max-w-2xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Main Photo */}
          <div>
            <label className="form-label">Foto Principal</label>
            {form.main_photo ? (
              <div className="relative aspect-video rounded-xl overflow-hidden">
                <img src={form.main_photo} alt="Principal" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => setForm({ ...form, main_photo: "" })}
                  className="absolute top-2 right-2 glass rounded-full p-2"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <PhotoUpload onUpload={handlePhotoUpload} folder={`main-${Date.now()}`} />
            )}
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Nombre *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="form-input"
                required
                data-testid="name-input"
              />
            </div>
            <div>
              <label className="form-label">Tipo de Cocina *</label>
              <select
                value={form.cuisine_type}
                onChange={(e) => setForm({ ...form, cuisine_type: e.target.value })}
                className="w-full h-[46px] px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 text-base"
                required
                data-testid="cuisine-input"
              >
                <option value="">Seleccionar...</option>
                {cuisineTypesList.map((type) => (
                  <option key={type.id} value={type.name}>{type.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Specialties */}
          <div>
            <label className="form-label">Especialidades</label>
            <div className="flex flex-wrap gap-2">
              {specialtiesList.map((specialty) => (
                <label
                  key={specialty.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                    form.specialties?.includes(specialty.name)
                      ? "bg-amber-500/20 border border-amber-500 text-amber-500"
                      : "bg-zinc-800 border border-zinc-700 text-zinc-400 hover:border-zinc-500"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={form.specialties?.includes(specialty.name) || false}
                    onChange={() => toggleSpecialty(specialty.name)}
                    className="hidden"
                  />
                  <span className="text-base">{specialty.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="form-label">Dirección *</label>
            <input
              type="text"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="form-input"
              required
              data-testid="address-input"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="form-label">Población *</label>
              <input
                type="text"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="form-input"
                required
                data-testid="city-input"
              />
            </div>
            <div>
              <label className="form-label">Provincia</label>
              <input
                type="text"
                value={form.province}
                onChange={(e) => setForm({ ...form, province: e.target.value })}
                className="form-input"
                data-testid="province-input"
              />
            </div>
            <div>
              <label className="form-label">Cód. Postal</label>
              <input
                type="text"
                value={form.postal_code}
                onChange={(e) => setForm({ ...form, postal_code: e.target.value.slice(0, 8) })}
                className="form-input"
                maxLength={8}
                data-testid="postal-code-input"
              />
            </div>
            <div>
              <label className="form-label">País *</label>
              <input
                type="text"
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="form-input"
                required
                data-testid="country-input"
              />
            </div>
          </div>

          {/* Contact */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Teléfono</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={form.phone_code || "+34"}
                  onChange={(e) => setForm({ ...form, phone_code: e.target.value })}
                  className="w-16 px-2 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-zinc-100 text-center text-sm"
                  placeholder="+34"
                  maxLength={4}
                  data-testid="phone-code-input"
                />
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
                    const formatted = digits.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
                    setForm({ ...form, phone: formatted });
                  }}
                  className="flex-1 px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-zinc-100 text-sm"
                  placeholder="000 000 000"
                  maxLength={11}
                  data-testid="phone-input"
                />
              </div>
            </div>
            <div>
              <label className="form-label">Sitio Web</label>
              <input
                type="url"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                className="form-input"
                placeholder="https://..."
                data-testid="website-input"
              />
            </div>
          </div>

          <div>
            <label className="form-label">Instagram</label>
            <input
              type="text"
              value={form.instagram}
              onChange={(e) => setForm({ ...form, instagram: e.target.value })}
              className="form-input"
              placeholder="@usuario"
              data-testid="instagram-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className="form-label">Observaciones</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="form-input min-h-[120px] text-base"
              placeholder="Observaciones"
              data-testid="description-input"
            />
          </div>

          {/* Dish Photos */}
          <div>
            <label className="form-label">Fotos de Platos</label>
            {form.dish_photos?.length > 0 && (
              <div className="photo-grid mb-4">
                {form.dish_photos.map((photo, i) => (
                  <div key={i} className="relative aspect-square rounded-xl overflow-hidden">
                    <img src={photo} alt={`Plato ${i + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeDishPhoto(i)}
                      className="absolute top-1 right-1 glass rounded-full p-1"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <PhotoUpload onUpload={handleDishPhotosUpload} folder={`dishes-${Date.now()}`} multiple />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-4 bg-amber-500 text-black font-semibold rounded-xl btn-glow disabled:opacity-50 text-lg"
            data-testid="submit-restaurant-btn"
          >
            {submitting ? "Guardando..." : isEdit ? "Guardar Cambios" : "Añadir Restaurante"}
          </button>
        </form>
      </main>
    </div>
  );
};

export default RestaurantFormPage;
